import React, { useRef } from "react";
import moment from "moment";
import ReactToPrint from "react-to-print";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper
} from "@mui/material";

import usePrintStyles from "../../../../hooks/usePrintStyles";
import { rupeesDisplayFull } from "../../../../helpers/util";
import { TSalary } from "../../../../interfaces/SalaryInterface";
import { Department } from "../../../../interfaces/DepartmentInterface";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import LabPrintLetterHead from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import { getNepaliMonths } from "../../../../components/Calendar/functions/calendarFunctions";
import LabPrintLetterHeadCentered from "../../../Lab/LabPrint/LabprintComponents/LabPrintLetterheadCentered";

// @ts-expect-error - svg
import Logo from "../../../../../assets/images/poweredByOkhati.svg";

function InfoRow({
  label,
  value,
  variant = "default"
}: {
  label: string;
  value: React.ReactNode;
  variant?: "default" | "summary";
}) {
  if (variant === "summary") {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", textTransform: "capitalize" }}>
        <Typography variant="body2">{label}:</Typography>
        <Typography variant="body2">{value}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700, minWidth: 130 }}>
        {label}
      </Typography>
      <Typography sx={{ mx: 0.5 }}>:</Typography>
      <Typography variant="body1" color="text.primary">
        {value}
      </Typography>
    </Box>
  );
}

export default function SalarySlip({
  salaryData,
  departments = [],
  buttonText = "Print"
}: {
  salaryData: TSalary;
  departments: Array<Department>;
  buttonText: string;
}): React.ReactNode {
  const printRef = useRef(null);
  const rc = useCurrentResourceCentre();
  usePrintStyles({ pageStyle: "size: A4; margin: 1mm 10mm" }).runPrintStyles();

  // Extract data from salaryData
  const employee = salaryData?.employee || salaryData?.serviceProvider;
  const earnings = salaryData?.earnings;
  const reimbursements = salaryData?.reimbursements ?? {};
  const basicSalary = earnings?.basicSalary || 0;
  const allowances = earnings?.allowances || {};
  const bonuses = earnings?.bonuses || {};
  const deductions = salaryData?.deductions || {};
  const salarySummary = salaryData?.salarySummary;
  const retirementContributionsSummary =
    salaryData?.salarySummary?.retirementContributions?.summary;

  const calendarSummary = salaryData?.calendarSummary;
  const leaveSummary = salaryData?.leaveSummary;
  const salaryMonth = salaryData?.payrollMonth;
  const salaryYear = salaryData?.payrollYear;

  const departmentName = employee?.departmentId
    ? departments.find((dept) => String(dept.id) === String(employee.departmentId))?.name || "N/A"
    : "N/A";

  const nepaliMonths = getNepaliMonths();
  const payMonthLabel = nepaliMonths.find((m) => m.month === salaryMonth)?.label || salaryMonth;

  const centralizedHeader: boolean = rc?.settings?.printSettings?.centralizedHeader ?? false;
  const clinicNameFontScale = rc?.settings?.printSettings.clinicNameFontScale;

  const panNo = rc?.settings?.billingSettings?.panNo;

  let letterHeadComponent = <Box />;
  if (centralizedHeader) {
    letterHeadComponent = (
      <LabPrintLetterHeadCentered
        resourceCentre={{
          ...rc,
          panNo,
          resourceCentreLogo: rc?.resourceCentreLogo ?? ""
        }}
        titleFontScale={clinicNameFontScale}
      />
    );
  } else {
    letterHeadComponent = (
      <LabPrintLetterHead
        resourceCentre={{
          ...rc,
          panNo,
          resourceCentreLogo: rc?.resourceCentreLogo ?? ""
        }}
        titleFontScale={clinicNameFontScale}
      />
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <ReactToPrint
        trigger={() => (
          <Button
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
              height: "100%"
            }}
          >
            {buttonText}
          </Button>
        )}
        content={() => printRef.current}
      />

      <iframe className="displayContents" title="printContainer">
        <div ref={printRef}>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", gap: 4 }}>
            {/* header */}
            <Box
              sx={{
                width: "100%",
                mt: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }}
            >
              {letterHeadComponent}

              <Typography
                variant="subtitle1"
                fontWeight={600}
                letterSpacing={1}
                sx={{ mt: "10px" }}
              >
                {payMonthLabel}, {salaryYear}
              </Typography>
            </Box>

            {/* employee detail */}
            <Box>
              <Typography
                sx={(theme) => ({
                  mb: 1,
                  fontWeight: 600,
                  borderBottom: `1px solid ${theme.palette.divider}`
                })}
              >
                Employee Detail
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box>
                  <InfoRow label="Name" value={`${employee?.firstName} ${employee?.lastName}`} />
                  {employee?.departmentId && <InfoRow label="Department" value={departmentName} />}
                  <InfoRow label="Designation" value={employee?.designation || "N/A"} />
                </Box>
                <Box>
                  <InfoRow
                    label="Date of Joining"
                    value={moment(employee?.dateOfJoining).format("DD MMM YYYY") || "N/A"}
                  />
                </Box>
              </Box>
            </Box>

            {/* Calendar Summary Section */}
            {calendarSummary && Object.keys(calendarSummary).length > 0 && (
              <Box>
                <Typography
                  sx={(theme) => ({
                    mb: 1.5,
                    fontWeight: 600,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  })}
                >
                  Calendar Summary
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)"
                  }}
                >
                  {Object.entries(calendarSummary).map(([key, val]) => (
                    <InfoRow
                      key={key}
                      label={
                        key === "businessDays"
                          ? "Working Days"
                          : key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")
                      }
                      value={val ?? 0}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Leave Summary Section */}
            {leaveSummary && Object.keys(leaveSummary).length > 0 && (
              <Box>
                <Typography
                  sx={(theme) => ({
                    mb: 1.5,
                    fontWeight: 600,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  })}
                >
                  Leave Summary
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)"
                  }}
                >
                  {Object.entries(leaveSummary).map(([key, val]) => (
                    <InfoRow
                      key={key}
                      label={
                        key.charAt(0).toUpperCase() +
                        key.slice(1).replace(/([A-Z])/g, " $1") +
                        (key.toLowerCase().endsWith("leave") || key.toLowerCase().endsWith("leaves")
                          ? ""
                          : " Leave")
                      }
                      value={val ?? 0}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* earnings, deductions, and reimbursements */}
            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
              <Table
                size="small"
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  borderCollapse: "collapse",
                  width: "100%"
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, background: "#fafbfc" }}>
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        textAlign: "right",
                        background: "#fafbfc"
                      }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* Earnings */}
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      sx={{
                        fontWeight: 700,
                        background: "#f5f5f5",
                        textTransform: "uppercase",
                        fontSize: 12
                      }}
                    >
                      Earnings
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>Basic Salary</TableCell>
                    <TableCell sx={{ textAlign: "right" }}>
                      {rupeesDisplayFull(basicSalary || 0)}
                    </TableCell>
                  </TableRow>

                  {Object.entries(allowances).map(([key, allowance]) =>
                    allowance?.value > 0 ? (
                      <TableRow key={key}>
                        <TableCell>
                          {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {rupeesDisplayFull(allowance.value)}
                        </TableCell>
                      </TableRow>
                    ) : null
                  )}

                  {Object.entries(bonuses).map(([key, bonus]) =>
                    bonus?.value > 0 ? (
                      <TableRow key={key}>
                        <TableCell>
                          {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {rupeesDisplayFull(bonus.value)}
                        </TableCell>
                      </TableRow>
                    ) : null
                  )}

                  {/* Deductions */}
                  {Object.values(deductions).some((item) => item?.value > 0) && (
                    <>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            fontWeight: 700,
                            background: "#f5f5f5",
                            textTransform: "uppercase",
                            fontSize: 12
                          }}
                        >
                          Deductions
                        </TableCell>
                      </TableRow>

                      {Object.entries(deductions).map(([key, deduction]) =>
                        deduction?.value > 0 ? (
                          <TableRow key={key}>
                            <TableCell>
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {rupeesDisplayFull(deduction.value)}
                            </TableCell>
                          </TableRow>
                        ) : null
                      )}
                    </>
                  )}

                  {/* Reimbursements */}
                  {Object.values(reimbursements).some((item) => item?.value > 0) && (
                    <>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            fontWeight: 700,
                            background: "#f5f5f5",
                            textTransform: "uppercase",
                            fontSize: 12
                          }}
                        >
                          Reimbursements
                        </TableCell>
                      </TableRow>

                      {Object.entries(reimbursements).map(([key, reimbursement]) =>
                        reimbursement?.value > 0 ? (
                          <TableRow key={key}>
                            <TableCell>
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {rupeesDisplayFull(reimbursement.value)}
                            </TableCell>
                          </TableRow>
                        ) : null
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* net payable section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "50%",
                ml: "auto"
              }}
            >
              <InfoRow
                label="Gross Monthly Salary"
                value={rupeesDisplayFull(salarySummary.salary?.grossMonthlySalary ?? 0)}
                variant="summary"
              />

              {(retirementContributionsSummary?.pfContributions?.employee ?? 0) > 0 && (
                <InfoRow
                  label="Employee PF contribution"
                  value={rupeesDisplayFull(
                    retirementContributionsSummary?.pfContributions?.employee ?? 0
                  )}
                  variant="summary"
                />
              )}

              {(retirementContributionsSummary?.ssfContributions?.employee ?? 0) > 0 && (
                <InfoRow
                  label="Employee SSF contribution"
                  value={rupeesDisplayFull(
                    retirementContributionsSummary?.ssfContributions?.employee ?? 0
                  )}
                  variant="summary"
                />
              )}

              {(retirementContributionsSummary?.citContributions?.employee ?? 0) > 0 && (
                <InfoRow
                  label="Employee CIT contribution"
                  value={rupeesDisplayFull(
                    retirementContributionsSummary?.citContributions?.employee ?? 0
                  )}
                  variant="summary"
                />
              )}

              {salarySummary?.tax?.netCurrentMonthTax > 0 && (
                <InfoRow
                  label="Monthly Tax Amount"
                  value={rupeesDisplayFull(salarySummary?.tax?.netCurrentMonthTax)}
                  variant="summary"
                />
              )}

              {salarySummary?.tax?.taxAdjustment !== 0 && (
                <InfoRow
                  label="Tax Adjustment"
                  value={rupeesDisplayFull(salarySummary?.tax?.taxAdjustment)}
                  variant="summary"
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Net Salary Monthly:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {rupeesDisplayFull(salarySummary?.salary?.netMonthlySalary ?? 0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Total Payable:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {rupeesDisplayFull(
                    salarySummary?.salary?.totalPayable ??
                      salarySummary?.salary?.netMonthlySalary ??
                      0
                  )}
                </Typography>
              </Box>
            </Box>

            {/* footer */}
            <Box
              className="billFooterPos"
              sx={(theme) => ({
                width: "100%",
                borderTop: `1px solid ${theme.palette.divider}`
              })}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1
                }}
              >
                <Box sx={{ height: "40px", ml: "auto" }}>
                  <img src={Logo} alt="logo" height="100%" width="100%" />
                </Box>
              </Box>
            </Box>
          </Box>
        </div>
      </iframe>
    </div>
  );
}
